import { useMemo, PropsWithChildren, useState } from "react";
import { Spin, theme } from "antd";
import { useDebounce } from "usehooks-ts";
import { useQuery } from "react-query";
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  EllipsisOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { DeepLinkType } from "pages/api/deep-links/types";
import { getEntities } from "pages/api-clients/graph-search";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { Hover } from "src/components/Hover";
import { useOrg } from "src/hooks/useOrg";
import { Flex } from "src/components/Flex";
import { DeepLink } from "src/models";
import { SparkleIcon } from "src/components/SparkleIcon";
import { askAtPersonToName, attributeToTitle } from "src/utils/strings";
import { TextHighlighter } from "src/components/TextHighlighter";
import { serializeParams } from "src/search/lib/params-state-reducer";
import { useApplicationContext } from "src/hooks/useApplication";
import { ExploreContext } from "./useExploreContext";
import { unserializedContext } from "./lib/serialization";
import { useStateHistory } from "./lib/useStateHistory";
import { useQueryToasterErrors } from "src/hooks/useToaster";
import { useRouter } from "next/router";
import { serializeDocument } from "./lib/useExploreDocument";
import { serializeContentView, ContentViews } from "./lib/useContentView";
import { serializeTabs } from "./lib/useTabs";

export function AutoCompleteResults(props: {
  context: ExploreContext;
  onSubmitSearch(term: string, select?: string): void;
  onSubmitQuestion(text: string): void;
  onOpenEntity(uuid: string): void;
  onClearAskInput?: () => void;
}) {
  return (
    <Flex
      start
      direction="column"
      gap={24}
      style={{
        padding: "16px 0",
        height: "60vh",
        maxHeight: "60vh",
        overflowY: "auto",
        overflowX: "hidden",
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      <QuestionSection
        context={props.context}
        onClearAskInput={props.onClearAskInput}
      />
      <SearchSection {...props} />
    </Flex>
  );
}

//TODO hiding for now. As we will want to restore but need to fix a display bug.
function RecentSection(props: {
  context: ExploreContext;
  onClearAskInput?: () => void;
}) {
  const orgId = useOrg().id;
  const { token } = theme.useToken();
  const { ask, params, doc, view, tabs } = props.context;
  const history = useStateHistory(orgId!, { params, doc, view, tabs });
  const filtered = useMemo(
    () =>
      history.items.filter((item) => {
        if (!ask.term?.trim()) return true;
        const ctx = unserializedContext(item.serialized);
        if (!ctx?.doc.title) return false;
        const lowerName = ctx.doc.title.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .every((word) => lowerName.includes(word.toLowerCase()));
      }),
    [history.items, ask.term]
  );
  const [more, setMore] = useState(false);
  const hasMore = !more && filtered.length > 3;
  const maxI = hasMore ? 3 : filtered.length;

  return filtered.length > 0 ? (
    <div style={{ width: "100%" }}>
      <span
        style={{
          fontWeight: 600,
          fontSize: 12,
          color: token.colorTextTertiary,
          textTransform: "uppercase",
          display: "block",
          textAlign: "left",
          width: "100%",
          paddingLeft: 8,
        }}
      >
        Session Activity
      </span>
      {filtered.slice(0, maxI).map((entry) => {
        const ctx = unserializedContext(entry.serialized);
        if (!ctx?.doc) return null;
        return (
          <LinkItem
            context={props.context}
            key={entry.id}
            id={entry.id}
            name={ctx.doc.title || "- untitled -"}
            serialized={serializeParams(ctx.params)}
            viz={ctx.view}
            modified={true}
            onClearAskInput={props.onClearAskInput}
          />
        );
      })}
      {hasMore && (
        <div
          style={{
            fontSize: 12,
            color: token.colorTextTertiary,
            padding: "2px 16px",
            textAlign: "left",
            cursor: "pointer",
          }}
          onClick={() => setMore(true)}
        >
          show all...
        </div>
      )}
    </div>
  ) : null;
}

function QuestionSection(props: {
  context: ExploreContext;
  onClearAskInput?: () => void;
}) {
  const { token } = theme.useToken();
  const { ask } = props.context;
  const {
    popular: popularQuestions = [],
    saved: savedQuestions = [],
    loading: questionsLoading,
  } = useDeepLinks(DeepLinkType.EXPLORE);
  const {
    popular: popularDigests = [],
    saved: savedDigests = [],
    loading: digestsLoading,
  } = useDeepLinks(DeepLinkType.DIGEST_2);
  const {
    popular: popularNarratives = [],
    saved: savedNarratives = [],
    loading: narrativesLoading,
  } = useDeepLinks(DeepLinkType.NARRATIVE);
  const {
    popular: popularBoards = [],
    saved: savedBoards = [],
    loading: boardsLoading,
  } = useDeepLinks(DeepLinkType.BOARD);

  const filteredSavedQuestions = useMemo(
    () =>
      savedQuestions.filter((item) => {
        if (!ask.term?.trim()) return true;
        const lowerName = item.name.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .filter(Boolean)
          .every((word) => {
            const lowerWord = word.toLowerCase();
            if (item.isBookmarked && lowerWord.startsWith("bookmark"))
              return true;
            return lowerName.includes(lowerWord);
          });
      }),
    [savedQuestions, ask.term]
  );

  const filteredPopularQuestions = useMemo(
    () =>
      popularQuestions.filter((item) => {
        if (!ask.term?.trim()) return true;
        const lowerName = item.name.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .filter(Boolean)
          .every((word) => {
            const lowerWord = word.toLowerCase();
            if (item.isBookmarked && lowerWord.startsWith("$bookmark"))
              return true;
            return lowerName.includes(lowerWord);
          });
      }),
    [popularQuestions, ask.term]
  );

  const filteredSavedDigests = useMemo(
    () =>
      savedDigests.filter((item) => {
        if (!ask.term?.trim()) return true;
        const lowerName = item.name.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .filter(Boolean)
          .every((word) => lowerName.includes(word.toLowerCase()));
      }),
    [savedDigests, ask.term]
  );

  const filteredPopularDigests = useMemo(
    () =>
      popularDigests.filter((item) => {
        if (!ask.term?.trim()) return true;
        const lowerName = item.name.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .filter(Boolean)
          .every((word) => lowerName.includes(word.toLowerCase()));
      }),
    [popularDigests, ask.term]
  );

  const filteredSavedNarratives = useMemo(
    () =>
      savedNarratives.filter((item) => {
        if (!ask.term?.trim()) return true;
        const lowerName = item.name.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .filter(Boolean)
          .every((word) => lowerName.includes(word.toLowerCase()));
      }),
    [savedNarratives, ask.term]
  );

  const filteredPopularNarratives = useMemo(
    () =>
      popularNarratives.filter((item) => {
        if (!ask.term?.trim()) return true;
        const lowerName = item.name.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .filter(Boolean)
          .every((word) => lowerName.includes(word.toLowerCase()));
      }),
    [popularNarratives, ask.term]
  );

  const filteredSavedBoards = useMemo(
    () =>
      savedBoards.filter((item) => {
        if (!ask.term?.trim()) return true;
        const lowerName = item.name.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .filter(Boolean)
          .every((word) => lowerName.includes(word.toLowerCase()));
      }),
    [savedBoards, ask.term]
  );

  const filteredPopularBoards = useMemo(
    () =>
      popularBoards.filter((item) => {
        if (!ask.term?.trim()) return true;
        const lowerName = item.name.toLocaleLowerCase();
        return ask.term
          .split(" ")
          .filter(Boolean)
          .every((word) => lowerName.includes(word.toLowerCase()));
      }),
    [popularBoards, ask.term]
  );

  if (
    questionsLoading &&
    digestsLoading &&
    narrativesLoading &&
    boardsLoading &&
    !filteredPopularQuestions?.length &&
    !filteredSavedQuestions?.length &&
    !filteredPopularDigests?.length &&
    !filteredSavedDigests?.length &&
    !filteredPopularNarratives?.length &&
    !filteredSavedNarratives?.length &&
    !filteredPopularBoards?.length &&
    !filteredSavedBoards?.length
  )
    return (
      <div style={{ textAlign: "left", padding: "8px 16px" }}>
        <Spin />
      </div>
    );

  if (
    filteredPopularQuestions.length === 0 &&
    filteredSavedQuestions.length === 0 &&
    filteredPopularDigests.length === 0 &&
    filteredSavedDigests.length === 0 &&
    filteredPopularNarratives.length === 0 &&
    filteredSavedNarratives.length === 0 &&
    filteredPopularBoards.length === 0 &&
    filteredSavedBoards.length === 0
  )
    return null;

  return (
    <div style={{ width: "100%" }}>
      {filteredSavedQuestions.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              fontSize: 10,
              color: token.colorTextTertiary,
              textTransform: "uppercase",
              display: "block",
              textAlign: "left",
              width: "100%",
              paddingLeft: 8,
              marginTop: 24,
            }}
          >
            Saved Questions
          </span>
          {filteredSavedQuestions.slice(0, 3).map((link) => (
            <DeepLinkItem
              context={props.context}
              key={link.id}
              link={link}
              onClearAskInput={props.onClearAskInput}
            />
          ))}
        </>
      )}
      {filteredPopularQuestions.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              fontSize: 10,
              color: token.colorTextTertiary,
              textTransform: "uppercase",
              display: "block",
              textAlign: "left",
              width: "100%",
              paddingLeft: 8,
              marginTop: 24,
            }}
          >
            Popular Questions
          </span>
          {filteredPopularQuestions.slice(0, 3).map((link) => (
            <DeepLinkItem
              context={props.context}
              key={link.id}
              link={link}
              onClearAskInput={props.onClearAskInput}
            />
          ))}
        </>
      )}
      {filteredSavedDigests.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              fontSize: 10,
              color: token.colorTextTertiary,
              textTransform: "uppercase",
              display: "block",
              textAlign: "left",
              width: "100%",
              paddingLeft: 8,
              marginTop: 24,
            }}
          >
            Saved Topics
          </span>
          {filteredSavedDigests.slice(0, 3).map((link) => (
            <DeepLinkItem
              context={props.context}
              key={link.id}
              link={link}
              onClearAskInput={props.onClearAskInput}
            />
          ))}
        </>
      )}
      {filteredPopularDigests.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              fontSize: 10,
              color: token.colorTextTertiary,
              textTransform: "uppercase",
              display: "block",
              textAlign: "left",
              width: "100%",
              paddingLeft: 8,
              marginTop: 24,
            }}
          >
            Popular Topics
          </span>
          {filteredPopularDigests.slice(0, 3).map((link) => (
            <DeepLinkItem
              context={props.context}
              key={link.id}
              link={link}
              onClearAskInput={props.onClearAskInput}
            />
          ))}
        </>
      )}
      {filteredSavedNarratives.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              fontSize: 10,
              color: token.colorTextTertiary,
              textTransform: "uppercase",
              display: "block",
              textAlign: "left",
              width: "100%",
              paddingLeft: 8,
              marginTop: 24,
            }}
          >
            Saved Narratives
          </span>
          {filteredSavedNarratives.slice(0, 3).map((link) => (
            <DeepLinkItem
              context={props.context}
              key={link.id}
              link={link}
              onClearAskInput={props.onClearAskInput}
            />
          ))}
        </>
      )}
      {filteredPopularNarratives.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              fontSize: 10,
              color: token.colorTextTertiary,
              textTransform: "uppercase",
              display: "block",
              textAlign: "left",
              width: "100%",
              paddingLeft: 8,
              marginTop: 24,
            }}
          >
            Popular Narratives
          </span>
          {filteredPopularNarratives.slice(0, 3).map((link) => (
            <DeepLinkItem
              context={props.context}
              key={link.id}
              link={link}
              onClearAskInput={props.onClearAskInput}
            />
          ))}
        </>
      )}
      {filteredSavedBoards.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              fontSize: 10,
              color: token.colorTextTertiary,
              textTransform: "uppercase",
              display: "block",
              textAlign: "left",
              width: "100%",
              paddingLeft: 8,
              marginTop: 24,
            }}
          >
            Saved Boards
          </span>
          {filteredSavedBoards.slice(0, 3).map((link) => (
            <DeepLinkItem
              context={props.context}
              key={link.id}
              link={link}
              onClearAskInput={props.onClearAskInput}
            />
          ))}
        </>
      )}
      {filteredPopularBoards.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              fontSize: 10,
              color: token.colorTextTertiary,
              textTransform: "uppercase",
              display: "block",
              textAlign: "left",
              width: "100%",
              paddingLeft: 8,
              marginTop: 24,
            }}
          >
            Popular Boards
          </span>
          {filteredPopularBoards.slice(0, 3).map((link) => (
            <DeepLinkItem
              context={props.context}
              key={link.id}
              link={link}
              onClearAskInput={props.onClearAskInput}
            />
          ))}
        </>
      )}
    </div>
  );
}

function SearchSection(props: {
  context: ExploreContext;
  onSubmitSearch(term: string, select?: string): void;
  onSubmitQuestion(text: string): void;
  onOpenEntity(uuid: string): void;
  onClearAskInput?: () => void;
}) {
  const app = useApplicationContext();
  const orgId = app.org_id!;
  const { ask } = props.context;
  const router = useRouter();
  const searchTerm = ask.term.trim();
  const submittedSearchTerm = useDebounce(searchTerm, 1500);
  const typing = submittedSearchTerm !== searchTerm;
  const matches = useQuery(
    ["explore/ask/menu/autocomplete", { submittedSearchTerm }],
    async () => getEntities({ search: submittedSearchTerm }, 1, 100, orgId),
    { enabled: Boolean(submittedSearchTerm) && !typing }
  );
  useQueryToasterErrors(matches);

  // Get narratives, boards, and topics for empty state
  const { popular: popularNarratives = [], saved: savedNarratives = [] } =
    useDeepLinks(DeepLinkType.NARRATIVE);
  const { popular: popularBoards = [], saved: savedBoards = [] } = useDeepLinks(
    DeepLinkType.BOARD
  );
  const { popular: popularQuestions = [], saved: savedQuestions = [] } =
    useDeepLinks(DeepLinkType.EXPLORE);

  const topMatches = new Map<string, { uuid: string; name: string }[]>();
  const entityTypes = new Set<string>();
  const pomos =
    matches.data?.data.map((obj) => ({
      uuid: ((obj?.attributes || ({} as any))["entityUuid"] ||
        (obj?.attributes || ({} as any))["Item.entityUuid"]) as string,
      name: (obj?.attributes || ({} as any))["Item.name"] as string,
      type: (obj?.attributes || ({} as any))["Item.EntityType"] as string,
    })) || [];
  pomos.forEach(({ uuid, name, type }) => {
    if (!entityTypes.has(type)) {
      entityTypes.add(type);
      topMatches.set(type, []);
    }
    topMatches.set(type, [...topMatches.get(type)!, { uuid, name }]);
  });
  const { token } = theme.useToken();
  const isLoading = matches.isLoading && submittedSearchTerm;
  const noResults =
    submittedSearchTerm && !isLoading && (matches.data?.data.length || 0) < 1;

  const displayTerm = askAtPersonToName(searchTerm);
  const searchSpan = (
    <span style={{ backgroundColor: token.colorHighlight }}>
      {displayTerm || "..."}
    </span>
  );

  // If no search term, show categories with limited items
  if (!searchTerm && !isLoading && !typing) {
    return (
      <div style={{ fontSize: 13, width: "100%" }}>
        {savedQuestions.length > 0 && (
          <>
            <div
              style={{
                fontWeight: 600,
                fontSize: 10,
                color: token.colorTextTertiary,
                textTransform: "uppercase",
                display: "block",
                textAlign: "left",
                width: "100%",
                paddingLeft: 8,
              }}
            >
              Recent Questions
            </div>
            {savedQuestions.slice(0, 3).map((link: DeepLink) => (
              <DeepLinkItem key={link.id} context={props.context} link={link} />
            ))}
          </>
        )}
        {savedNarratives.length > 0 && (
          <>
            <div
              style={{
                fontWeight: 600,
                fontSize: 10,
                color: token.colorTextTertiary,
                textTransform: "uppercase",
                display: "block",
                textAlign: "left",
                width: "100%",
                paddingLeft: 8,
                marginTop: 16,
              }}
            >
              Recent Narratives
            </div>
            {savedNarratives.slice(0, 3).map((link: DeepLink) => (
              <DeepLinkItem key={link.id} context={props.context} link={link} />
            ))}
          </>
        )}
        {savedBoards.length > 0 && (
          <>
            <div
              style={{
                fontWeight: 600,
                fontSize: 10,
                color: token.colorTextTertiary,
                textTransform: "uppercase",
                display: "block",
                textAlign: "left",
                width: "100%",
                paddingLeft: 8,
                marginTop: 16,
              }}
            >
              Recent Boards
            </div>
            {savedBoards.slice(0, 3).map((link: DeepLink) => (
              <DeepLinkItem key={link.id} context={props.context} link={link} />
            ))}
          </>
        )}
      </div>
    );
  }

  const handleEntityClick = (uuid: string, name: string) => {
    const d = encodeURIComponent(serializeDocument({ title: name }));
    const t = encodeURIComponent(serializeTabs({ fullscreen: true }));
    const url = `/${orgId}/entity/${uuid}?&d=${d}&t=${t}`;
    app.appendTab(name, url, { open: true });
    ask.open(false);
    ask.setQuestion("");
    props.onClearAskInput?.();
  };

  return submittedSearchTerm || searchTerm || isLoading || typing ? (
    <div style={{ fontSize: 11, width: "100%" }}>
      {(submittedSearchTerm || isLoading || typing) && (
        <div
          style={{
            fontWeight: 600,
            fontSize: 10,
            color: token.colorTextTertiary,
            textTransform: "uppercase",
            display: "block",
            textAlign: "left",
            width: "100%",
            paddingLeft: 8,
            marginTop: 24,
          }}
        >
          Open Search Options
        </div>
      )}
      {searchTerm && (
        <ItemRow>
          <Flex
            start
            style={{ alignItems: "center", width: "100%" }}
            gap={8}
            onClick={() => props.onSubmitQuestion(searchTerm)}
          >
            <Flex
              centered
              style={{
                width: 16,
                height: 16,
                backgroundColor: token.colorPrimary,
                color: token.colorTextLightSolid,
                marginTop: 2,
                marginBottom: 2,
                flexShrink: 0,
              }}
            >
              <SparkleIcon />
            </Flex>
            <div style={{ flex: 1, textAlign: "left" }}>
              Generate a question with {searchSpan}
            </div>
          </Flex>
        </ItemRow>
      )}
      {searchTerm && (
        <ItemRow>
          <Flex
            start
            style={{ fontWeight: 500, alignItems: "center", width: "100%" }}
            onClick={() => props.onSubmitSearch(searchTerm)}
          >
            <Flex
              centered
              style={{
                backgroundColor: token.colorBgLayout,
                borderRadius: token.borderRadiusXS,
                justifyContent: "center",
                alignItems: "center",
                width: 16,
                height: 16,
                marginTop: 2,
                marginBottom: 2,
                marginRight: 8,
                flexShrink: 0,
              }}
            >
              <SearchOutlined style={{ fontSize: 10 }} />
            </Flex>
            <div style={{ flex: 1, textAlign: "left" }}>
              Find anything with {searchSpan}
            </div>
          </Flex>
        </ItemRow>
      )}
      {(typing || isLoading || noResults) && (
        <ItemRow>
          <Flex start style={{ width: "100%", alignItems: "center" }}>
            <div
              style={{
                backgroundColor: token.colorBgLayout,
                borderRadius: token.borderRadiusXS,
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                width: 16,
                height: 16,
                marginTop: 2,
                marginBottom: 2,
                marginRight: 8,
                flexShrink: 0,
              }}
            >
              {typing ? (
                <span style={{ fontSize: 10 }}>
                  <EllipsisOutlined className="pulse" />
                </span>
              ) : isLoading ? (
                <LoadingOutlined style={{ fontSize: 10 }} />
              ) : (
                <SearchOutlined style={{ fontSize: 10 }} />
              )}
            </div>
            <em
              style={{
                flex: 1,
                fontWeight: 400,
                fontSize: 11,
                color: token.colorTextTertiary,
                textAlign: "left",
              }}
            >
              {typing ? "typing..." : isLoading ? "searching..." : "no matches"}
            </em>
          </Flex>
        </ItemRow>
      )}
      {!typing &&
        [...entityTypes].map((type) => (
          <div key={type} style={{ marginTop: 24 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 10,
                color: token.colorTextTertiary,
                textTransform: "uppercase",
                display: "block",
                textAlign: "left",
                width: "100%",
                paddingLeft: 8,
              }}
            >
              {attributeToTitle(type)} ({topMatches.get(type)?.length})
            </div>
            {[...(topMatches.get(type) || [])].slice(0, 3).map((o) => (
              <ItemRow key={o.uuid}>
                <Flex
                  start
                  style={{
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                  onClick={() => handleEntityClick(o.uuid, o.name)}
                >
                  <div
                    style={{
                      flex: 1,
                      fontSize: 11,
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    <TextHighlighter
                      text={o.name}
                      terms={searchTerm.trim().split(" ")}
                    />
                  </div>
                  <ArrowRightOutlined
                    style={{ fontSize: 10, color: token.colorTextTertiary }}
                  />
                </Flex>
              </ItemRow>
            ))}
          </div>
        ))}
    </div>
  ) : null;
}

function DeepLinkItem({
  context,
  link,
  onClearAskInput,
}: {
  context: ExploreContext;
  link: DeepLink;
  onClearAskInput?: () => void;
}) {
  const { ask } = context;
  const router = useRouter();
  const app = useApplicationContext();
  const orgId = router.query.orgId as string;
  const { token } = theme.useToken();

  const getTypeIcon = () => {
    switch (link.type) {
      case DeepLinkType.EXPLORE: {
        return <FileTextOutlined />;
      }
      case DeepLinkType.DIGEST_2: {
        return <UnorderedListOutlined />;
      }
      case DeepLinkType.NARRATIVE: {
        return <BookOutlined />;
      }
      case DeepLinkType.BOARD: {
        return <AppstoreOutlined />;
      }
      default: {
        return null;
      }
    }
  };

  let deepState = link.state;
  let viz: undefined | { value: string; config: any } = undefined;
  try {
    const json = JSON.parse(link.state);
    deepState = json.serialized;
    viz = (json.viz || []).at(0);
  } catch {}

  const handleClick = () => {
    var url = "";

    switch (link.type) {
      case DeepLinkType.EXPLORE: {
        const s = encodeURIComponent(deepState);
        const d = encodeURIComponent(serializeDocument({ title: link.name }));
        const v = encodeURIComponent(
          serializeContentView(
            viz ? { value: viz.value as ContentViews, config: viz.config } : {}
          )
        );
        const t = encodeURIComponent(serializeTabs({ fullscreen: true }));
        url = `/${orgId}/explore?s=${s}&d=${d}&t=${t}&v=${v}`;
        break;
      }
      case DeepLinkType.DIGEST_2: {
        url = `/${orgId}/topics/${link.id}`;
        break;
      }
      case DeepLinkType.NARRATIVE: {
        url = `/${orgId}/reports/${link.id}`;
        break;
      }
      case DeepLinkType.BOARD: {
        url = `/${orgId}/boards/${link.id}`;
        break;
      }
    }

    app.appendTab(link.name, url, { open: true });

    ask.open(false);
    ask.setQuestion("");
    onClearAskInput && onClearAskInput();
  };

  return (
    <ItemRow>
      <div onClick={handleClick} style={{ width: "100%" }}>
        <Flex
          start
          gap={8}
          style={{
            alignItems: "center",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Flex start gap={8} style={{ alignItems: "center", flex: 1 }}>
            <Flex
              centered
              style={{
                marginBottom: 2,
                marginTop: 2,
                width: 16,
                height: 16,
                border: `solid 1px ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius,
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {getTypeIcon()}
            </Flex>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              <TextHighlighter
                text={link.name}
                terms={ask.term.trim().split(" ")}
              />
            </div>
          </Flex>
          <ArrowRightOutlined
            style={{ fontSize: 10, color: token.colorTextTertiary }}
          />
        </Flex>
      </div>
    </ItemRow>
  );
}

function LinkItem(props: {
  context: ExploreContext;
  id: string;
  name: string;
  serialized: string;
  viz?: any;
  modified?: boolean;
  onClearAskInput?: () => void;
}) {
  const { ask, doc } = props.context;
  const { token } = theme.useToken();
  const router = useRouter();
  const orgId = router.query.orgId as string;

  return (
    <ItemRow>
      <div
        onClick={() => {
          const s = encodeURIComponent(props.serialized);
          const d = encodeURIComponent(
            serializeDocument({ title: props.name })
          );
          const v = encodeURIComponent(serializeContentView(props.viz || {}));
          const t = encodeURIComponent(serializeTabs({ fullscreen: true }));
          const url = `/${orgId}/explore?s=${s}&d=${d}&t=${t}&v=${v}`;

          router.push({
            pathname: "/[orgId]/t/new",
            query: { orgId, title: props.name, url },
          });

          ask.open(false);
          ask.setQuestion("");
          props.onClearAskInput && props.onClearAskInput();
        }}
      >
        <Flex
          start
          gap={8}
          style={{
            alignItems: "center",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Flex start gap={8} style={{ alignItems: "center", flex: 1 }}>
            <Flex
              centered
              style={{
                marginBottom: 2,
                marginTop: 2,
                width: 16,
                height: 16,
                border: `solid 1px ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius,
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <FileTextOutlined />
            </Flex>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                fontStyle: props.modified ? "italic" : undefined,
                textAlign: "left",
              }}
            >
              <TextHighlighter
                text={props.name}
                terms={ask.term.trim().split(" ")}
              />
            </div>
          </Flex>
          <ArrowRightOutlined
            style={{ fontSize: 10, color: token.colorTextTertiary }}
          />
        </Flex>
      </div>
    </ItemRow>
  );
}

function ItemRow(props: PropsWithChildren) {
  const { token } = theme.useToken();
  return (
    <Hover
      baseStyle={{
        width: "100%",
        borderTop: `solid 1px ${token.colorBgLayout}`,
        fontSize: 11,
        fontWeight: 500,
        cursor: "pointer",
        padding: "2px 16px",
      }}
      hoverStyle={{
        backgroundColor: token.colorFillTertiary,
        color: token.colorPrimaryText,
      }}
    >
      <div style={{ width: "100%" }}>{props.children}</div>
    </Hover>
  );
}
